import { Stack } from '@mui/material'
import { TrendColorizer } from 'components/TrendColorizer'
import { useAtomValue } from 'jotai'
import { memo } from 'react'
import { StringFormatter } from 'utils/StringFormatter'
import { PerpSymbol } from '../../api/v1/market'
import { PerpPositionRecord } from '../../api/v1/perp'
import { PRIMARY_CURRENCY } from '../../constants/currencies'
import { usePerpSize } from '../../hooks/useSizeCalculations'
import { usePerpSymbols } from '../../store/perp/hooks'
import { OrderDirection } from '../../store/spot/types'
import { toFixed } from '../../utils/number'
import { getOrderSide } from '../../utils/order'
import { findSymbolByName } from '../../utils/symbols'
import {
  orderModalAtom,
  OrderModalData,
  useCloseOrderModal,
  useUpdateModalTitle,
} from './index'
import {
  CancelButton,
  OrderModalRow,
  OrderModalRowInfo,
  OrderModalRowLabel,
} from './styles'
import { DateFormatter } from '../../utils/datetime'

export const PerpPositionModal = memo<{
  openOrderCancel?: (data: OrderModalData) => void
}>(({ openOrderCancel }) => {
  const orderData = useAtomValue(orderModalAtom)
  const perpSymbols = usePerpSymbols()
  const closeModal = useCloseOrderModal()

  const data = orderData?.data as PerpPositionRecord
  useUpdateModalTitle(data?.symbol)

  const symbol = findSymbolByName(perpSymbols, data.symbol)

  const {
    baseCurrency = '',
    quoteCurrency = '',
    baseScale = 0,
    quoteScale = 0,
    multiplier = 0,
  } = symbol ?? ({} as PerpSymbol)

  const {
    id = 0,
    direction = OrderDirection.LONG,
    quantity = 0,
    entryPrice = 0,
    entryValue = 0,
    requiredMargin = 0,
    minimumMaintenanceMargin = 0,
    realizedPNL = 0,
    unrealizedPNL = 0,
    updatedAt = 0,
  } = data

  const size = usePerpSize(quantity, multiplier)

  const fixedSize = toFixed(size, baseScale)
  const fixedPrice = toFixed(entryPrice, quoteScale)

  const datetime = DateFormatter.toISODateTime(updatedAt)

  return (
    <Stack>
      <OrderModalRow>
        <OrderModalRowLabel>
          <div>Size</div>
          <div>Entry Price</div>
        </OrderModalRowLabel>
        <OrderModalRowInfo>
          <div className={getOrderSide(direction, 'lowerCase')}>
            {fixedSize} {baseCurrency}
          </div>
          <div>{fixedPrice}</div>
        </OrderModalRowInfo>
      </OrderModalRow>
      <OrderModalRow>
        <OrderModalRowLabel>
          <div>Side</div>
          <div>Entry Value</div>
        </OrderModalRowLabel>
        <OrderModalRowInfo>
          <div className={getOrderSide(direction, 'lowerCase')}>
            {StringFormatter.startCase(direction)}
          </div>
          <div>
            {entryValue} {PRIMARY_CURRENCY}
          </div>
        </OrderModalRowInfo>
      </OrderModalRow>
      <OrderModalRow>
        <OrderModalRowLabel>
          <div>Margin</div>
          <div>U-Pnl</div>
        </OrderModalRowLabel>
        <OrderModalRowInfo>
          <div>
            {requiredMargin} {PRIMARY_CURRENCY}
          </div>
          <TrendColorizer
            trend={unrealizedPNL > 0}
            value={`${StringFormatter.numberSign(
              unrealizedPNL,
            )} ${PRIMARY_CURRENCY}`}
          />
        </OrderModalRowInfo>
      </OrderModalRow>

      <OrderModalRow>
        <OrderModalRowLabel>
          <div>Update Time</div>
          <div>Realized Pnl</div>
        </OrderModalRowLabel>
        <OrderModalRowInfo>
          <div>{datetime}</div>
          <TrendColorizer
            trend={realizedPNL > 0}
            value={`${StringFormatter.numberSign(
              realizedPNL,
            )} ${PRIMARY_CURRENCY}`}
          />
        </OrderModalRowInfo>
      </OrderModalRow>
      <CancelButton
        onClick={() => {
          openOrderCancel && openOrderCancel(data as any)
          closeModal && closeModal()
        }}
      >
        Close Position
      </CancelButton>
    </Stack>
  )
})
